import { apiRequest } from './client';
import type { Paginated } from '@loanaudit/shared';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

export interface NotificationEntryDTO {
  id: string;
  source: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  linkPath: string | null;
  details: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationListResponse extends Paginated<NotificationEntryDTO> {
  unread: number;
}

export interface NotificationPrefs {
  emailOnCritical: boolean;
  emailOnWarning: boolean;
  digestEnabled: boolean;
  digestTimeOfDay: string;
  updatedAt: string;
}

export const notificationsApi = {
  list: (params: { page: number; pageSize: number; unreadOnly?: boolean }) =>
    apiRequest<NotificationListResponse>('/api/notifications/mine', { query: params }),
  unreadCount: () => apiRequest<{ count: number }>('/api/notifications/unread-count'),
  markRead: (ids: string[]) =>
    apiRequest<{ updated: number }>('/api/notifications/mark-read', {
      method: 'POST',
      body: { ids },
    }),
  markAllRead: () =>
    apiRequest<{ updated: number }>('/api/notifications/mark-all-read', { method: 'POST' }),
  remove: (id: string) =>
    apiRequest<{ deleted: true }>(`/api/notifications/${id}`, { method: 'DELETE' }),

  getPrefs: () => apiRequest<NotificationPrefs>('/api/notifications/preferences'),
  updatePrefs: (body: Partial<Omit<NotificationPrefs, 'updatedAt'>>) =>
    apiRequest<NotificationPrefs>('/api/notifications/preferences', {
      method: 'PATCH',
      body,
    }),

  broadcast: (body: {
    recipients: string[] | 'all';
    source: string;
    severity?: NotificationSeverity;
    title: string;
    body?: string;
    linkPath?: string;
    details?: Record<string, unknown>;
  }) =>
    apiRequest<{ sent: number }>('/api/notifications/broadcast', {
      method: 'POST',
      body,
    }),
};
